<template>
  <div class="accesos-metricas">
    <!-- Estado de carga -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <template v-else-if="hasData">
      <!-- ===== KPIs ===== -->
      <section class="metric-section">
        <div class="kpi-grid">
          <div
            v-for="kpi in kpiCards"
            :key="kpi.key"
            class="kpi-card"
            :class="{ 'kpi-card--wide': kpi.wide }"
            :style="{ borderTopColor: kpi.color }"
          >
            <div class="kpi-icon" :style="{ color: kpi.color, backgroundColor: hexToRgba(kpi.color, 0.12) }">
              <i :class="kpi.icon"></i>
            </div>
            <div class="kpi-body">
              <div class="kpi-value" :class="{ 'kpi-value--fit': kpi.wide }">{{ kpi.value }}</div>
              <div class="kpi-label">{{ kpi.label }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== Tendencias temporales ===== -->
      <section class="metric-section">
        <h5 class="section-heading">
          <i class="fas fa-chart-line"></i> Tendencias temporales
        </h5>
        <div class="charts-grid grid-12">
          <div class="chart-card span-8">
            <button class="chart-expand-btn" title="Ver en pantalla completa" @click="openFullscreen('trend')"><i class="fas fa-expand"></i></button>
            <h6 class="chart-title">Tendencia diaria de accesos</h6>
            <div class="chart-canvas-wrapper">
              <canvas ref="trendChart"></canvas>
            </div>
          </div>
          <div class="chart-card span-4">
            <button class="chart-expand-btn" title="Ver en pantalla completa" @click="openFullscreen('state')"><i class="fas fa-expand"></i></button>
            <h6 class="chart-title">Ingresos vs Salidas</h6>
            <div class="chart-canvas-wrapper">
              <canvas ref="stateChart"></canvas>
            </div>
          </div>
          <div class="chart-card span-8">
            <button class="chart-expand-btn" title="Ver en pantalla completa" @click="openFullscreen('hour')"><i class="fas fa-expand"></i></button>
            <h6 class="chart-title">Accesos por hora del día</h6>
            <div class="chart-canvas-wrapper">
              <canvas ref="hourChart"></canvas>
            </div>
          </div>
          <div class="chart-card span-4">
            <button class="chart-expand-btn" title="Ver en pantalla completa" @click="openFullscreen('weekday')"><i class="fas fa-expand"></i></button>
            <h6 class="chart-title">Accesos por día de la semana</h6>
            <div class="chart-canvas-wrapper">
              <canvas ref="weekdayChart"></canvas>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== Distribución ===== -->
      <section class="metric-section">
        <h5 class="section-heading">
          <i class="fas fa-layer-group"></i> Distribución de accesos
        </h5>
        <div class="charts-grid grid-6">
          <div class="chart-card span-2">
            <button class="chart-expand-btn" title="Ver en pantalla completa" @click="openFullscreen('method')"><i class="fas fa-expand"></i></button>
            <h6 class="chart-title">Por método de acceso</h6>
            <div class="chart-canvas-wrapper">
              <canvas ref="methodChart"></canvas>
            </div>
          </div>
          <div class="chart-card span-2">
            <button class="chart-expand-btn" title="Ver en pantalla completa" @click="openFullscreen('profile')"><i class="fas fa-expand"></i></button>
            <h6 class="chart-title">Por perfil de persona</h6>
            <div class="chart-canvas-wrapper">
              <canvas ref="profileChart"></canvas>
            </div>
          </div>
          <div class="chart-card span-2">
            <button class="chart-expand-btn" title="Ver en pantalla completa" @click="openFullscreen('point')"><i class="fas fa-expand"></i></button>
            <h6 class="chart-title">Por punto de acceso</h6>
            <div class="chart-canvas-wrapper">
              <canvas ref="pointChart"></canvas>
            </div>
          </div>
          <div class="chart-card span-3">
            <button class="chart-expand-btn" title="Ver en pantalla completa" @click="openFullscreen('branch')"><i class="fas fa-expand"></i></button>
            <h6 class="chart-title">Por sucursal</h6>
            <div class="chart-canvas-wrapper">
              <canvas ref="branchChart"></canvas>
            </div>
          </div>
          <div class="chart-card span-3">
            <button class="chart-expand-btn" title="Ver en pantalla completa" @click="openFullscreen('plan')"><i class="fas fa-expand"></i></button>
            <h6 class="chart-title">Por plan / servicio de acceso</h6>
            <div class="chart-canvas-wrapper">
              <canvas ref="planChart"></canvas>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== Top de personas ===== -->
      <section class="metric-section">
        <h5 class="section-heading">
          <i class="fas fa-trophy"></i> Top personas por número de accesos
        </h5>
        <div class="chart-card">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0 top-persons-table">
              <thead>
                <tr>
                  <th style="width: 48px;">#</th>
                  <th>Persona</th>
                  <th class="d-none d-md-table-cell">RUT</th>
                  <th class="d-none d-sm-table-cell">Perfil</th>
                  <th class="text-end">Accesos</th>
                  <th class="text-end d-none d-sm-table-cell">Ingresos</th>
                  <th class="text-end d-none d-sm-table-cell">Salidas</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, idx) in topPersons" :key="p.rutSocio + '-' + idx">
                  <td><span class="rank-badge">{{ idx + 1 }}</span></td>
                  <td>
                    <div class="fw-semibold">{{ p.nombrePersona }}</div>
                    <div class="d-md-none text-muted small">{{ p.rutSocio }}</div>
                  </td>
                  <td class="d-none d-md-table-cell">{{ p.rutSocio }}</td>
                  <td class="d-none d-sm-table-cell"><span class="badge bg-light text-dark">{{ p.perfilPersona }}</span></td>
                  <td class="text-end fw-bold">{{ p.value }}</td>
                  <td class="text-end text-success d-none d-sm-table-cell">{{ p.ingresos }}</td>
                  <td class="text-end text-danger d-none d-sm-table-cell">{{ p.salidas }}</td>
                </tr>
                <tr v-if="!topPersons.length">
                  <td colspan="7" class="text-center text-muted py-4">Sin datos en el rango seleccionado.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <p v-if="timezone" class="text-muted small text-end mt-3 mb-0">
        <i class="fas fa-clock"></i> Horarios calculados en zona horaria: <strong>{{ timezone }}</strong>
      </p>
    </template>

    <!-- Sin datos -->
    <div v-else class="text-center py-5 text-muted">
      <i class="fas fa-chart-bar fa-3x mb-3 d-block" style="opacity: 0.3;"></i>
      <p class="mb-0">No hay datos de acceso para los filtros seleccionados.</p>
    </div>

    <!-- Modal "Pantalla completa": gráfico ampliado + tabla de datos -->
    <div v-if="fullscreen.show" class="accesos-modal-overlay" @click.self="closeFullscreen">
      <div class="accesos-modal accesos-modal--fs">
        <div class="accesos-modal-header">
          <h6 class="mb-0"><i class="fas fa-chart-bar me-2"></i>{{ fullscreen.title }}</h6>
          <button class="btn-close-custom" @click="closeFullscreen"><i class="fas fa-times"></i></button>
        </div>
        <div class="accesos-modal-body">
          <div class="fs-chart-wrapper">
            <canvas ref="fsChart"></canvas>
          </div>
          <div class="table-responsive mt-4">
            <table class="table table-sm table-hover align-middle mb-0 fs-table">
              <thead>
                <tr>
                  <th
                    v-for="(h, i) in fullscreen.headers"
                    :key="i"
                    :class="{ 'text-end': i >= 1 }"
                  >{{ h }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, ri) in fullscreen.rows" :key="ri">
                  <td
                    v-for="(cell, ci) in row"
                    :key="ci"
                    :class="{ 'text-end': ci >= 1, 'fw-semibold': ci === 0 }"
                  >{{ cell }}</td>
                </tr>
                <tr v-if="!fullscreen.rows.length">
                  <td :colspan="fullscreen.headers.length" class="text-center text-muted py-3">Sin datos.</td>
                </tr>
              </tbody>
              <tfoot v-if="fullscreen.rows.length">
                <tr class="fs-total-row">
                  <td
                    v-for="(cell, ci) in fullscreen.totals"
                    :key="ci"
                    :class="{ 'text-end': ci >= 1 }"
                  >{{ cell }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        <div class="accesos-modal-footer">
          <button class="btn btn-danger" :disabled="fsExporting" @click="exportFsPDF">
            <i class="fas fa-file-pdf me-2"></i> Descargar en PDF
          </button>
          <button class="btn btn-primary" :disabled="fsExporting" @click="exportFsImage">
            <i class="fas fa-download me-2"></i> Descargar como imagen
          </button>
          <button class="btn btn-light" @click="closeFullscreen">Cerrar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js';

// Paleta de colores reutilizable para las series categóricas
const PALETTE = [
  '#6bd098', '#66C4DE', '#fcc468', '#ef8157', '#9368E9',
  '#51bcda', '#f17e5d', '#7ac29a', '#fbc658', '#5e72e4',
  '#11cdef', '#fb6340', '#2dce89', '#f5365c', '#8965e0'
];

const WEEKDAY_ORDER = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

// Plugin inline (Chart.js 2.x) que dibuja el valor de cada dato directamente
// sobre el gráfico, para que sean visibles sin pasar el mouse. Maneja barras
// verticales/horizontales, líneas y doughnut. No altera la estructura del
// gráfico: sólo pinta texto en la fase afterDatasetsDraw.
const valueLabelsPlugin = {
  afterDatasetsDraw(chart) {
    const ctx = chart.ctx;
    const type = chart.config.type;
    ctx.save();
    chart.data.datasets.forEach((dataset, di) => {
      const meta = chart.getDatasetMeta(di);
      if (meta.hidden) return;
      meta.data.forEach((element, index) => {
        const raw = dataset.data[index];
        if (raw === null || raw === undefined) return;
        const num = Number(raw);
        const label = isNaN(num) ? String(raw) : num.toLocaleString('es-CL');
        const model = element._model;

        if (type === 'doughnut' || type === 'pie') {
          if (!num) return;
          // Omitir segmentos demasiado pequeños para evitar saturación.
          if ((model.endAngle - model.startAngle) < 0.18) return;
          const mid = (model.startAngle + model.endAngle) / 2;
          const r = (model.outerRadius + model.innerRadius) / 2;
          const x = model.x + Math.cos(mid) * r;
          const y = model.y + Math.sin(mid) * r;
          ctx.font = 'bold 11px Arial';
          ctx.fillStyle = '#fff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(label, x, y);
        } else if (type === 'horizontalBar') {
          ctx.font = 'bold 11px Arial';
          ctx.fillStyle = '#5a5a5a';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(label, model.x + 4, model.y);
        } else {
          // Barras verticales y líneas: valor encima del punto/barra.
          ctx.font = 'bold 11px Arial';
          ctx.fillStyle = '#5a5a5a';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.fillText(label, model.x, model.y - 4);
        }
      });
    });
    ctx.restore();
  }
};

export default {
  name: 'AccesosMetricas',
  props: {
    // Objeto `result` completo de GetGymAccessMetrics ({ kpis, charts, meta })
    metrics: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    timezone: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      chartInstances: {},
      fsInstance: null,
      fsExporting: false,
      fullscreen: { show: false, title: '', headers: [], rows: [], totals: [] }
    };
  },
  computed: {
    hasData() {
      return !!(this.metrics && this.metrics.kpis);
    },
    kpis() {
      return (this.metrics && this.metrics.kpis) || {};
    },
    charts() {
      return (this.metrics && this.metrics.charts) || {};
    },
    topPersons() {
      return this.charts.topPersons || [];
    },
    kpiCards() {
      const k = this.kpis;
      return [
        { key: 'totalAccesos', label: 'Total accesos', value: this.fmt(k.totalAccesos), icon: 'fas fa-door-open', color: '#6bd098' },
        { key: 'totalIngresos', label: 'Ingresos', value: this.fmt(k.totalIngresos), icon: 'fas fa-sign-in-alt', color: '#66C4DE' },
        { key: 'totalSalidas', label: 'Salidas', value: this.fmt(k.totalSalidas), icon: 'fas fa-sign-out-alt', color: '#ef8157' },
        { key: 'personasUnicas', label: 'Personas únicas', value: this.fmt(k.personasUnicas), icon: 'fas fa-users', color: '#9368E9' },
        { key: 'sucursalesActivas', label: 'Sucursales activas', value: this.fmt(k.sucursalesActivas), icon: 'fas fa-store', color: '#fcc468' },
        { key: 'diasConActividad', label: 'Días con actividad', value: this.fmt(k.diasConActividad), icon: 'fas fa-calendar-check', color: '#51bcda' },
        { key: 'promedioAccesosPorDia', label: 'Prom. accesos/día', value: this.fmt(k.promedioAccesosPorDia), icon: 'fas fa-chart-line', color: '#7ac29a' },
        { key: 'horaPico', label: 'Hora pico', value: k.horaPico || '—', icon: 'fas fa-clock', color: '#fb6340' },
        { key: 'metodoMasUsado', label: 'Método más usado', value: k.metodoMasUsado || '—', icon: 'fas fa-fingerprint', color: '#5e72e4', wide: true },
        { key: 'planMasUtilizado', label: 'Plan más usado', value: k.planMasUtilizado || '—', icon: 'fas fa-id-card', color: '#2dce89', wide: true }
      ];
    }
  },
  watch: {
    metrics: {
      handler() {
        this.$nextTick(() => this.renderAll());
      },
      deep: true
    }
  },
  mounted() {
    this.$nextTick(() => this.renderAll());
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    this.destroyAll();
    this.destroyFsChart();
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    fmt(n) {
      if (n === null || n === undefined || n === '') return '—';
      const num = Number(n);
      if (isNaN(num)) return n;
      return num.toLocaleString('es-CL');
    },

    // Convierte un color HEX (#rrggbb) a rgba() con la opacidad indicada.
    // Se usa para el fondo translúcido del ícono de cada KPI.
    hexToRgba(hex, alpha) {
      if (!hex) return `rgba(107, 208, 152, ${alpha})`;
      const h = hex.replace('#', '');
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    destroyAll() {
      Object.values(this.chartInstances).forEach((c) => {
        try { c && c.destroy(); } catch (e) { /* noop */ }
      });
      this.chartInstances = {};
    },

    renderAll() {
      if (!this.hasData) {
        this.destroyAll();
        return;
      }
      this.destroyAll();
      this.chartDefs().forEach((def) => this.makeChart(def.ref, def.build()));
      this.fitWideKpis();
    },

    onResize() {
      this.fitWideKpis();
    },

    // Reduce el tamaño de fuente del valor en las tarjetas KPI anchas
    // (método/plan más usado) hasta que el texto quepa en una sola línea.
    fitWideKpis() {
      this.$nextTick(() => {
        if (!this.$el || !this.$el.querySelectorAll) return;
        const els = this.$el.querySelectorAll('.kpi-value--fit');
        els.forEach((el) => this.fitText(el));
      });
    },

    fitText(el) {
      if (!el) return;
      // Partir desde el tamaño definido por CSS (respeta breakpoints) y reducir.
      el.style.fontSize = '';
      const max = parseFloat(window.getComputedStyle(el).fontSize) || 22;
      const min = 11;
      let size = max;
      let guard = 0;
      while (el.scrollWidth > el.clientWidth && size > min && guard < 40) {
        size -= 1;
        el.style.fontSize = size + 'px';
        guard++;
      }
    },

    // Inyecta el plugin de etiquetas de valor y reserva padding interno para que
    // no se recorten en los bordes. Compartido por las tarjetas y el modal.
    augmentConfig(config) {
      config.plugins = (config.plugins || []).concat(valueLabelsPlugin);
      config.options = config.options || {};
      config.options.layout = config.options.layout || {};
      const pad = config.options.layout.padding || {};
      if (config.type === 'bar' || config.type === 'line') {
        pad.top = Math.max(pad.top || 0, 16);
      } else if (config.type === 'horizontalBar') {
        pad.right = Math.max(pad.right || 0, 30);
      }
      config.options.layout.padding = pad;
      return config;
    },

    makeChart(refName, config) {
      const canvas = this.$refs[refName];
      if (!canvas) return;
      this.chartInstances[refName] = new Chart(canvas.getContext('2d'), this.augmentConfig(config));
    },

    colorsFor(n) {
      const out = [];
      for (let i = 0; i < n; i++) out.push(PALETTE[i % PALETTE.length]);
      return out;
    },

    baseBarOptions(horizontal = false) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          [horizontal ? 'xAxes' : 'yAxes']: [{
            ticks: { beginAtZero: true, precision: 0 }
          }]
        }
      };
    },

    // Registro único de gráficos: cada definición provee su config de Chart.js
    // (build) y los metadatos de su tabla (headers/rows/numericFrom). Se usa
    // tanto para pintar la tarjeta como para el modal "pantalla completa".
    chartDefs() {
      // Genera rawRows para una distribución simple [label, value].
      const distRaw = (arr) => arr.map((d) => [d.label, Number(d.value) || 0]);
      const weekday = (this.charts.accessByWeekday || []).slice().sort((a, b) => {
        const ia = a.dow != null ? a.dow : WEEKDAY_ORDER.indexOf(a.label) + 1;
        const ib = b.dow != null ? b.dow : WEEKDAY_ORDER.indexOf(b.label) + 1;
        return ia - ib;
      });

      return [
        {
          key: 'trend',
          ref: 'trendChart',
          title: 'Tendencia diaria de accesos',
          build: () => {
            const data = this.charts.accessByDay || [];
            return {
              type: 'line',
              data: {
                labels: data.map((d) => d.label),
                datasets: [
                  {
                    label: 'Ingresos',
                    data: data.map((d) => (d.ingresos != null ? d.ingresos : d.value)),
                    borderColor: '#66C4DE',
                    backgroundColor: 'rgba(102,196,222,0.15)',
                    fill: true, tension: 0.3, pointRadius: 2
                  },
                  {
                    label: 'Salidas',
                    data: data.map((d) => d.salidas || 0),
                    borderColor: '#ef8157',
                    backgroundColor: 'rgba(239,129,87,0.10)',
                    fill: true, tension: 0.3, pointRadius: 2
                  }
                ]
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                legend: { display: true, position: 'bottom' },
                scales: { yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }] }
              }
            };
          },
          table: {
            headers: ['Fecha', 'Ingresos', 'Salidas', 'Total', '%'],
            pctCol: 3,
            rawRows: () => (this.charts.accessByDay || []).map((d) => [
              d.label,
              Number(d.ingresos != null ? d.ingresos : d.value) || 0,
              Number(d.salidas) || 0,
              Number(d.value) || 0
            ])
          }
        },
        {
          key: 'state',
          ref: 'stateChart',
          title: 'Ingresos vs Salidas',
          build: () => {
            const data = this.charts.accessByState || [];
            return {
              type: 'doughnut',
              data: {
                labels: data.map((d) => d.label),
                datasets: [{
                  data: data.map((d) => d.value),
                  backgroundColor: data.map((d) => (d.label === 'INGRESO' ? '#6bd098' : '#ef8157'))
                }]
              },
              options: { responsive: true, maintainAspectRatio: false, legend: { position: 'bottom' } }
            };
          },
          table: {
            headers: ['Estado', 'Cantidad', '%'],
            pctCol: 1,
            rawRows: () => distRaw(this.charts.accessByState || [])
          }
        },
        {
          key: 'hour',
          ref: 'hourChart',
          title: 'Accesos por hora del día',
          build: () => {
            const raw = this.charts.accessByHour || [];
            const byHour = {};
            raw.forEach((d) => { byHour[d.hour] = d; });
            const labels = [];
            const values = [];
            for (let h = 0; h < 24; h++) {
              labels.push(String(h).padStart(2, '0') + ':00');
              values.push(byHour[h] ? byHour[h].value : 0);
            }
            return {
              type: 'bar',
              data: { labels, datasets: [{ label: 'Accesos', data: values, backgroundColor: '#66C4DE' }] },
              options: this.baseBarOptions()
            };
          },
          table: {
            headers: ['Hora', 'Accesos', 'Ingresos', 'Salidas', '%'],
            pctCol: 1,
            rawRows: () => (this.charts.accessByHour || []).slice().sort((a, b) => a.hour - b.hour).map((d) => [
              d.label, Number(d.value) || 0, Number(d.ingresos) || 0, Number(d.salidas) || 0
            ])
          }
        },
        {
          key: 'weekday',
          ref: 'weekdayChart',
          title: 'Accesos por día de la semana',
          build: () => ({
            type: 'bar',
            data: {
              labels: weekday.map((d) => d.label),
              datasets: [{ label: 'Accesos', data: weekday.map((d) => d.value), backgroundColor: '#9368E9' }]
            },
            options: this.baseBarOptions()
          }),
          table: {
            headers: ['Día', 'Accesos', '%'],
            pctCol: 1,
            rawRows: () => distRaw(weekday)
          }
        },
        {
          key: 'method',
          ref: 'methodChart',
          title: 'Por método de acceso',
          build: () => {
            const data = this.charts.accessByMethod || [];
            return {
              type: 'doughnut',
              data: { labels: data.map((d) => d.label), datasets: [{ data: data.map((d) => d.value), backgroundColor: this.colorsFor(data.length) }] },
              options: { responsive: true, maintainAspectRatio: false, legend: { position: 'bottom' } }
            };
          },
          table: {
            headers: ['Método', 'Accesos', '%'],
            pctCol: 1,
            rawRows: () => distRaw(this.charts.accessByMethod || [])
          }
        },
        {
          key: 'profile',
          ref: 'profileChart',
          title: 'Por perfil de persona',
          build: () => {
            const data = this.charts.accessByProfile || [];
            return {
              type: 'doughnut',
              data: { labels: data.map((d) => d.label), datasets: [{ data: data.map((d) => d.value), backgroundColor: this.colorsFor(data.length) }] },
              options: { responsive: true, maintainAspectRatio: false, legend: { position: 'bottom' } }
            };
          },
          table: {
            headers: ['Perfil', 'Accesos', '%'],
            pctCol: 1,
            rawRows: () => distRaw(this.charts.accessByProfile || [])
          }
        },
        {
          key: 'point',
          ref: 'pointChart',
          title: 'Por punto de acceso',
          build: () => {
            const data = this.charts.accessByPoint || [];
            return {
              type: 'bar',
              data: { labels: data.map((d) => d.label), datasets: [{ label: 'Accesos', data: data.map((d) => d.value), backgroundColor: '#fcc468' }] },
              options: this.baseBarOptions()
            };
          },
          table: {
            headers: ['Punto de acceso', 'Accesos', '%'],
            pctCol: 1,
            rawRows: () => distRaw(this.charts.accessByPoint || [])
          }
        },
        {
          key: 'branch',
          ref: 'branchChart',
          title: 'Por sucursal',
          build: () => {
            const data = this.charts.accessByBranch || [];
            return {
              type: 'horizontalBar',
              data: { labels: data.map((d) => d.label), datasets: [{ label: 'Accesos', data: data.map((d) => d.value), backgroundColor: '#51bcda' }] },
              options: this.baseBarOptions(true)
            };
          },
          table: {
            headers: ['Sucursal', 'Accesos', '%'],
            pctCol: 1,
            rawRows: () => distRaw(this.charts.accessByBranch || [])
          }
        },
        {
          key: 'plan',
          ref: 'planChart',
          title: 'Por plan / servicio de acceso',
          build: () => {
            const data = this.charts.accessByPlan || [];
            return {
              type: 'horizontalBar',
              data: { labels: data.map((d) => d.label), datasets: [{ label: 'Accesos', data: data.map((d) => d.value), backgroundColor: '#2dce89' }] },
              options: this.baseBarOptions(true)
            };
          },
          table: {
            headers: ['Plan / servicio', 'Accesos', '%'],
            pctCol: 1,
            rawRows: () => distRaw(this.charts.accessByPlan || [])
          }
        }
      ];
    },

    // Abre el modal de pantalla completa para el gráfico indicado.
    // Construye las filas con su columna de % (sobre el total de la columna base)
    // y una fila de totales que cuadra con el totalizado de las métricas.
    openFullscreen(key) {
      const def = this.chartDefs().find((d) => d.key === key);
      if (!def) return;

      const t = def.table;
      const headers = t.headers;
      const pctCol = t.pctCol;
      const raw = t.rawRows();
      const numCols = headers.length - 1; // columnas previas a la de % (= largo de rawRow)

      // Total de la columna base para el cálculo de porcentajes.
      const pctTotal = raw.reduce((s, r) => s + (Number(r[pctCol]) || 0), 0);
      const fmtPct = (v) => (pctTotal ? (Math.round((v / pctTotal) * 1000) / 10) + '%' : '0%');

      // Filas de display: label sin formato, numéricas con separador de miles, % al final.
      const rows = raw.map((r) => {
        const cells = r.map((c, i) => (i === 0 ? c : this.fmt(c)));
        cells.push(fmtPct(Number(r[pctCol]) || 0));
        return cells;
      });

      // Fila de totales: suma de cada columna numérica + 100% en la columna de %.
      const totals = ['TOTAL'];
      for (let i = 1; i < numCols; i++) {
        const sum = raw.reduce((s, r) => s + (Number(r[i]) || 0), 0);
        totals.push(this.fmt(sum));
      }
      totals.push(raw.length ? '100%' : '0%');

      this.fullscreen = { show: true, title: def.title, headers, rows, totals };

      this.$nextTick(() => {
        this.destroyFsChart();
        const canvas = this.$refs.fsChart;
        if (canvas) {
          this.fsInstance = new Chart(canvas.getContext('2d'), this.augmentConfig(def.build()));
        }
      });
    },

    closeFullscreen() {
      this.destroyFsChart();
      this.fullscreen = { show: false, title: '', headers: [], rows: [], totals: [] };
    },

    destroyFsChart() {
      if (this.fsInstance) {
        try { this.fsInstance.destroy(); } catch (e) { /* noop */ }
        this.fsInstance = null;
      }
    },

    // Descarga el gráfico del modal como imagen PNG (con fondo blanco), misma
    // técnica que gymsales.
    async exportFsImage() {
      try {
        const original = this.$refs.fsChart;
        if (!original) return;

        const temp = document.createElement('canvas');
        temp.width = original.width;
        temp.height = original.height;
        const ctx = temp.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, temp.width, temp.height);
        ctx.drawImage(original, 0, 0);

        temp.toBlob((blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${this.fullscreen.title} - ${new Date().toLocaleDateString('es-CL')}.png`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          this.$swal.fire({
            icon: 'success',
            title: 'Imagen descargada',
            text: 'El gráfico se descargó como imagen PNG con fondo blanco.',
            timer: 2000,
            showConfirmButton: false
          });
        }, 'image/png');
      } catch (error) {
        this.$logger.error('Error al exportar imagen del gráfico:', error);
        this.$swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo descargar la imagen.', confirmButtonColor: '#6bd098' });
      }
    },

    // Genera un PDF con el gráfico ampliado + la tabla de datos, misma técnica
    // que gymsales (jsPDF + html2canvas, con paginación de la tabla si es alta).
    async exportFsPDF() {
      this.fsExporting = true;
      try {
        this.$swal.fire({
          title: '¡Generando PDF!',
          html: `
            <div class="text-center">
              <p class="mb-4">Procesando el gráfico "${this.fullscreen.title}"…</p>
              <div class="spinner-border text-primary" role="status"></div>
            </div>
          `,
          showConfirmButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false
        });

        const [{ default: html2canvas }, jsPDFModule] = await Promise.all([
          import('html2canvas'),
          import('jspdf')
        ]);
        const jsPDF = jsPDFModule.jsPDF || jsPDFModule.default || jsPDFModule;

        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 15;
        const contentWidth = pageWidth - margin * 2;
        let yPosition = margin;

        // 1. Encabezado
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('REPORTE DE ANÁLISIS - REGISTROS DE ACCESO', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 10;
        pdf.setFontSize(14);
        pdf.text(this.fullscreen.title.toUpperCase(), pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 10;
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        const fechaGeneracion = new Date().toLocaleDateString('es-CL', {
          year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
        pdf.text(`Generado: ${fechaGeneracion}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 8;
        if (this.timezone) {
          pdf.text(`Zona horaria: ${this.timezone}`, pageWidth / 2, yPosition, { align: 'center' });
          yPosition += 8;
        } else {
          yPosition += 2;
        }

        // 2. Gráfico (con fondo blanco)
        const chartCanvas = this.$refs.fsChart;
        if (chartCanvas) {
          const temp = document.createElement('canvas');
          temp.width = chartCanvas.width;
          temp.height = chartCanvas.height;
          const ctx = temp.getContext('2d');
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, temp.width, temp.height);
          ctx.drawImage(chartCanvas, 0, 0);
          const imgData = temp.toDataURL('image/png');
          const chartHeight = (temp.height * contentWidth) / temp.width;
          if (yPosition + chartHeight > pageHeight - margin - 20) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.addImage(imgData, 'PNG', margin, yPosition, contentWidth, chartHeight);
          yPosition += chartHeight + 10;
        }

        // 3. Tabla de datos (clonada para capturar todo el contenido sin scroll)
        const tableElement = this.$el.querySelector('.accesos-modal .fs-table');
        if (tableElement) {
          if (yPosition > pageHeight - margin - 40) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Datos detallados del gráfico', margin, yPosition);
          yPosition += 8;

          const clone = tableElement.cloneNode(true);
          const container = document.createElement('div');
          container.style.position = 'absolute';
          container.style.left = '-9999px';
          container.style.top = '-9999px';
          container.style.width = '1100px';
          container.style.backgroundColor = '#ffffff';
          container.style.padding = '20px';
          container.appendChild(clone);
          const style = document.createElement('style');
          style.textContent = `
            table { width: 100%; border-collapse: collapse; color: #212529; font-family: Arial, sans-serif; }
            th, td { padding: 8px 10px; border-top: 1px solid #dee2e6; font-size: 13px; }
            thead th { border-bottom: 2px solid #dee2e6; font-weight: bold; background:#f8f9fa; text-transform: uppercase; }
            tbody tr:nth-child(odd) { background:#f6f7f9; }
            tfoot td { border-top: 2px solid #ccc; font-weight: bold; background:#eef0f2; }
            .text-end { text-align: right; }
          `;
          container.appendChild(style);
          document.body.appendChild(container);

          const tableCanvas = await html2canvas(container, {
            backgroundColor: '#ffffff', scale: 2, logging: false,
            windowWidth: 1100, windowHeight: container.scrollHeight, useCORS: true, allowTaint: true
          });
          document.body.removeChild(container);

          const tableHeight = (tableCanvas.height * contentWidth) / tableCanvas.width;
          const maxTableHeight = pageHeight - yPosition - margin - 15;

          if (tableHeight > maxTableHeight) {
            // Dividir la tabla en varias páginas.
            const scale = contentWidth / tableCanvas.width;
            const pageHeightInCanvas = maxTableHeight / scale;
            let currentY = 0;
            while (currentY < tableCanvas.height) {
              if (currentY > 0) {
                pdf.addPage();
                yPosition = margin;
                pdf.setFontSize(10);
                pdf.setFont('helvetica', 'italic');
                pdf.text('Datos detallados del gráfico (continuación)', margin, yPosition);
                yPosition += 8;
              }
              const segmentHeight = Math.min(pageHeightInCanvas, tableCanvas.height - currentY);
              const segment = document.createElement('canvas');
              segment.width = tableCanvas.width;
              segment.height = segmentHeight;
              segment.getContext('2d').drawImage(
                tableCanvas, 0, currentY, tableCanvas.width, segmentHeight, 0, 0, tableCanvas.width, segmentHeight
              );
              pdf.addImage(segment.toDataURL('image/png'), 'PNG', margin, yPosition, contentWidth, segmentHeight * scale);
              currentY += segmentHeight;
              yPosition += segmentHeight * scale;
            }
          } else {
            pdf.addImage(tableCanvas.toDataURL('image/png'), 'PNG', margin, yPosition, contentWidth, tableHeight);
            yPosition += tableHeight;
          }
        }

        // 4. Footer en todas las páginas
        const totalPages = pdf.internal.getNumberOfPages();
        const totalRegistros = this.fullscreen.rows.length;
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          pdf.text(`Total de filas: ${totalRegistros}`, margin, pageHeight - 5);
          pdf.text('Informe generado desde CentralGym App - https://centralgym.cl', pageWidth / 2, pageHeight - 5, { align: 'center' });
          pdf.text(`Página ${i} de ${totalPages}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
        }

        const chartName = this.fullscreen.title.replace(/[^a-z0-9]/gi, '_');
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        pdf.save(`Grafico_${chartName}_${timestamp}.pdf`);

        this.$swal.close();
        await new Promise((resolve) => setTimeout(resolve, 300));
        this.$swal.fire({
          icon: 'success',
          title: '¡PDF generado!',
          text: `El informe del gráfico "${this.fullscreen.title}" se descargó correctamente.`,
          confirmButtonColor: '#6bd098',
          timer: 4000,
          timerProgressBar: true
        });
      } catch (error) {
        this.$logger.error('Error al generar PDF del gráfico:', error);
        this.$swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo generar el PDF.', confirmButtonColor: '#6bd098' });
      } finally {
        this.fsExporting = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
$card-radius: 14px;
$card-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
$card-shadow-hover: 0 6px 20px rgba(0, 0, 0, 0.10);

/* ===== Secciones ===== */
.metric-section {
  margin-bottom: 28px;

  &:last-of-type {
    margin-bottom: 0;
  }
}

.section-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #66615b;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ececec;

  i {
    color: #6bd098;
    font-size: 16px;
  }
}

/* ===== Grids propios (independientes del grid de Bootstrap, que entra en
   conflicto con Paper Dashboard). Usan `gap` para garantizar separación
   horizontal y vertical consistente en todos los breakpoints. ===== */
.kpi-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(2, minmax(0, 1fr)); /* móvil */
}

.charts-grid {
  display: grid;
  gap: 24px;
  grid-template-columns: minmax(0, 1fr); /* móvil: apilado */
}

@media (min-width: 576px) {
  .kpi-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

@media (min-width: 768px) {
  .charts-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (min-width: 992px) {
  .kpi-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}

@media (min-width: 1200px) {
  /* Distribuciones a 12/6 columnas reales con spans */
  .charts-grid.grid-12 { grid-template-columns: repeat(12, minmax(0, 1fr)); }
  .charts-grid.grid-6 { grid-template-columns: repeat(6, minmax(0, 1fr)); }

  .charts-grid .span-8 { grid-column: span 8; }
  .charts-grid .span-4 { grid-column: span 4; }
  .charts-grid .span-3 { grid-column: span 3; }
  .charts-grid .span-2 { grid-column: span 2; }
}

@media (min-width: 1400px) {
  .kpi-grid { grid-template-columns: repeat(6, minmax(0, 1fr)); }
}

/* ===== Tarjetas KPI ===== */
.kpi-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border-radius: $card-radius;
  border-top: 3px solid #6bd098;
  padding: 18px;
  box-shadow: $card-shadow;
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: $card-shadow-hover;
  }
}

/* KPIs con valor de texto largo (método/plan más usado): ocupan 2 columnas. */
.kpi-card--wide {
  grid-column: span 2;
}

.kpi-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  font-size: 22px;
  flex-shrink: 0;
}

.kpi-body {
  min-width: 0;
}

.kpi-value {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.15;
  color: #2c2c2c;
  word-break: break-word;
}

/* Valor de las tarjetas anchas: una sola línea, se reduce vía JS (fitText). */
.kpi-value--fit {
  white-space: nowrap;
  overflow: hidden;
  max-width: 100%;
}

.kpi-label {
  font-size: 12px;
  color: #9a9a9a;
  margin-top: 2px;
  line-height: 1.2;
}

/* ===== Tarjetas de gráfico ===== */
.chart-card {
  position: relative;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: $card-radius;
  padding: 20px;
  box-shadow: $card-shadow;
  height: 100%;
  transition: box-shadow 0.18s ease;

  &:hover {
    box-shadow: $card-shadow-hover;
  }
}

/* Botón "Ver en pantalla completa" en la esquina superior derecha de cada gráfico. */
.chart-expand-btn {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 8px;
  background: #f4f4f4;
  color: #9a9a9a;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
  z-index: 2;

  &:hover {
    background: #6bd098;
    color: #fff;
  }
}

.chart-title {
  font-weight: 600;
  font-size: 14px;
  color: #66615b;
  margin-bottom: 16px;
  padding-bottom: 10px;
  padding-right: 38px; /* espacio para el botón de expandir */
  border-bottom: 1px solid #f2f2f2;
}

.chart-canvas-wrapper {
  position: relative;
  flex: 1 1 auto;
  height: 300px;
  min-height: 300px;
}

/* ===== Tabla Top personas ===== */
.top-persons-table {
  thead th {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #9a9a9a;
    border-bottom: 2px solid #ececec;
  }

  tbody td {
    vertical-align: middle;
  }
}

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #f4f4f4;
  color: #66615b;
  font-weight: 700;
  font-size: 13px;
}

/* ===== Responsividad ===== */
/* Tablets: gráficos algo más bajos para entrar mejor en columnas dobles */
@media (max-width: 991.98px) {
  .chart-canvas-wrapper {
    height: 260px;
    min-height: 260px;
  }

  .section-heading {
    font-size: 14px;
  }
}

/* Móviles: tarjetas a pantalla completa, alturas reducidas y KPIs compactos */
@media (max-width: 575.98px) {
  .metric-section {
    margin-bottom: 22px;
  }

  .chart-card {
    padding: 16px;
  }

  .chart-canvas-wrapper {
    height: 230px;
    min-height: 230px;
  }

  .kpi-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    padding: 14px;
  }

  .kpi-icon {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .kpi-value {
    font-size: 18px;
  }
}

/* ===== Modal "Pantalla completa" ===== */
.accesos-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1060;
  padding: 16px;
}

.accesos-modal {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-height: 94vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
}

.accesos-modal--fs {
  max-width: 920px;
}

.accesos-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  color: #66615b;
  flex-shrink: 0;
}

.btn-close-custom {
  border: none;
  background: transparent;
  color: #9a9a9a;
  font-size: 18px;
  cursor: pointer;
  line-height: 1;

  &:hover { color: #66615b; }
}

.accesos-modal-body {
  padding: 18px 20px;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
}

.fs-chart-wrapper {
  position: relative;
  height: 46vh;
  min-height: 300px;
}

.fs-table {
  thead th {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #9a9a9a;
    border-bottom: 2px solid #ececec;
    white-space: nowrap;
  }

  tbody td {
    vertical-align: middle;
  }

  /* Filas intercaladas (zebra), como en las tablas de métricas de gymsales. */
  tbody tr:nth-child(odd) {
    background: #f6f7f9;
  }

  tbody tr:nth-child(even) {
    background: #ffffff;
  }

  .fs-total-row td {
    border-top: 2px solid #ececec;
    font-weight: 700;
    color: #2c2c2c;
    background: #eef0f2;
  }
}

.accesos-modal-footer {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #eee;
  flex-shrink: 0; /* permanece visible mientras el cuerpo hace scroll */
}

@media (max-width: 575.98px) {
  .fs-chart-wrapper {
    height: 38vh;
    min-height: 240px;
  }
}
</style>
